import { hasRole, type EndpointDto, type Role } from '@agent-platform/shared';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { EmptyState } from '@/components/empty-state';
import { EndpointConversations } from '@/components/endpoint-conversations';
import { ErrorAlert } from '@/components/error-alert';
import { PageHeader } from '@/components/page-header';
import { RequireTenant } from '@/components/require-tenant';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { usePaged } from '@/lib/queries';

function ConversationsContent({ tenantId, role }: { tenantId: string; role: Role }) {
  const canEdit = hasRole(role, 'admin');
  const endpoints = usePaged<EndpointDto>(['endpoints', tenantId], '/endpoints', canEdit);
  const list = endpoints.data?.pages.flatMap((p) => p.items) ?? [];
  const [selected, setSelected] = useState<string | null>(null);
  // Defaults to the first endpoint that keeps history.
  const endpoint =
    list.find((e) => e.id === selected) ??
    list.find((e) => e.conversationHistory.enabled) ??
    list[0];

  return (
    <>
      <PageHeader
        title="Conversation history"
        description={
          <>
            Chats that endpoints keep for their callers, per user and scope (header-keyed history,
            turned on in an endpoint’s{' '}
            <Link to="/endpoints" className="underline">
              Settings
            </Link>
            ).
          </>
        }
      />
      {!canEdit ? (
        <EmptyState title="Admins only">
          Conversation history holds end users’ chats, so it needs the admin role.
        </EmptyState>
      ) : endpoints.error ? (
        <ErrorAlert error={endpoints.error} />
      ) : endpoints.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : !endpoint ? (
        <EmptyState title="No endpoints yet">
          Create an endpoint and turn on conversation history in its Settings.
        </EmptyState>
      ) : (
        <>
          <div className="mb-4 grid w-80 gap-1.5">
            <Label htmlFor="conversations-endpoint">Endpoint</Label>
            <Select value={endpoint.id} onValueChange={setSelected}>
              <SelectTrigger id="conversations-endpoint" aria-label="Endpoint">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {list.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.agentName} · {e.publicId}
                    {e.conversationHistory.enabled ? '' : ' (history off)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <EndpointConversations
            key={endpoint.id}
            tenantId={tenantId}
            endpoint={endpoint}
            canDelete
          />
        </>
      )}
    </>
  );
}

export function ConversationsPage() {
  return (
    <RequireTenant>
      {(tenant) => <ConversationsContent tenantId={tenant.id} role={tenant.role} />}
    </RequireTenant>
  );
}
